/**
 * Tag style mappings.
 *
 * @internal Used by Tag atom only.
 */

import type { TagIntent, TagPalette, TagSize, TagVariant } from './Tag.types';

/** Solid: filled background (standard palette = white text, brand = dark text) */
const standardFillStyles: Record<TagIntent, string> = {
  primary: 'bg-purple',
  secondary: 'bg-dark-invirtus',
  info: 'bg-blue',
  success: 'bg-green',
  warning: 'bg-yellow',
  error: 'bg-red',
};

const brandFillStyles: Record<TagIntent, string> = {
  primary: 'bg-purple',
  secondary: 'bg-dark-invirtus',
  info: 'bg-blue',
  success: 'bg-purple',
  warning: 'bg-light-lin',
  error: 'bg-brand-red',
};

/** Outline: transparent/slight bg, colored border and text */
const outlineStyles: Record<TagIntent, string> = {
  primary: 'border border-purple bg-purple/10 text-purple',
  secondary: 'border border-dark-invirtus bg-dark-invirtus/10 text-dark-invirtus',
  info: 'border border-blue bg-blue/10 text-blue',
  success: 'border border-green bg-green/10 text-green',
  warning: 'border border-yellow bg-yellow/10 text-yellow',
  error: 'border border-red bg-red/10 text-red',
};

/** Subtle: soft pastel background */
const subtleStyles: Record<TagIntent, string> = {
  primary: 'bg-purple-soft text-dark-invirtus',
  secondary: 'bg-dark-invirtus-soft text-dark-invirtus',
  info: 'bg-blue-soft text-dark-invirtus',
  success: 'bg-green-soft text-dark-invirtus',
  warning: 'bg-yellow-soft text-dark-invirtus',
  error: 'bg-red-soft text-dark-invirtus',
};

export const fillStyles: Record<TagPalette, Record<TagIntent, string>> = {
  standard: standardFillStyles,
  brand: brandFillStyles,
};

export const textColorStyles: Record<TagPalette, string> = {
  standard: 'text-white',
  brand: 'text-dark-invirtus',
};

/**
 * Size floors (sm 20 · md 24 · lg 32 · xl 40).
 * Uses min-h so height can grow when `wrap` lets the label break.
 */
export const sizeStyles: Record<TagSize, string> = {
  sm: 'min-h-5 px-2 py-0.5 text-[9px] leading-none tracking-normal gap-1',
  md: 'min-h-6 px-2.5 py-1 text-[10px] leading-[13.8px] tracking-normal gap-1.5',
  lg: 'min-h-8 px-3.5 py-1.5 text-sm leading-none tracking-normal gap-2',
  xl: 'min-h-10 px-4 py-2 text-base leading-none tracking-normal gap-2.5',
};

/** Maps Tag size to IconWrapper size for leading icon */
export const iconSizeMap: Record<TagSize, 'xs' | 'sm' | 'md'> = {
  sm: 'xs',
  md: 'xs',
  lg: 'sm',
  xl: 'md',
};

/** Close button: fixed square size for perfect icon centering */
export const closeButtonStyles: Record<TagSize, string> = {
  sm: 'w-3 h-3 min-w-3 min-h-3',
  md: 'w-3.5 h-3.5 min-w-3.5 min-h-3.5',
  lg: 'w-4 h-4 min-w-4 min-h-4',
  xl: 'w-5 h-5 min-w-5 min-h-5',
};

export function getVariantStyles(
  variant: TagVariant,
  intent: TagIntent,
  palette: TagPalette
): string {
  if (variant === 'solid') {
    return `${fillStyles[palette][intent]} ${textColorStyles[palette]}`;
  }
  if (variant === 'outline') {
    return outlineStyles[intent];
  }
  return subtleStyles[intent];
}
